/**
 * New engagement.
 *
 * Attaches an existing CRM account to a service plan. It does not create the
 * account — a client is a client across the whole application, and duplicating
 * them per module is how two sources of truth start.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ServiceLine, type ServicePlan } from '@/lib/api';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';

interface AccountRow { id: string; name: string }
interface UserRow { id: string; firstName: string; lastName: string; role: string; isActive: boolean }

export function EngagementFormDialog({ open, onOpenChange }: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();

  const [accountId, setAccountId] = useState('');
  const [serviceLineId, setServiceLineId] = useState('');
  const [planId, setPlanId] = useState('');
  const [assignedToId, setAssignedToId] = useState('');
  const [startDate, setStartDate] = useState(() => {
    // Default to the first of the current month: a cycle covers a whole period,
    // so starting mid-month would produce a short first cycle.
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  });
  const [monthlyPrice, setMonthlyPrice] = useState('');

  const { data: accountsData } = useQuery({
    queryKey: ['accounts', 'for-engagement'],
    queryFn: () => api.getAccounts({ limit: 100 }),
    enabled: open,
  });
  const { data: linesData } = useQuery({
    queryKey: ['svc', 'service-lines'],
    queryFn: () => api.getServiceLines(),
    enabled: open,
  });
  const { data: plansData } = useQuery({
    queryKey: ['svc', 'plans', serviceLineId],
    queryFn: () => api.getServicePlans({ serviceLineId: serviceLineId || undefined }),
    enabled: open && !!serviceLineId,
  });
  // /api/users is admin-only. A manager creating an engagement simply gets an
  // empty list and leaves it unassigned rather than seeing an error — the
  // assignee can be set later from the engagement itself.
  const { data: usersData } = useQuery({
    queryKey: ['users', 'for-engagement'],
    queryFn: () => api.listUsers({ limit: 100 }),
    enabled: open,
    retry: false,
  });

  const accounts = (accountsData?.data ?? []) as AccountRow[];
  const lines = (linesData?.data ?? []) as ServiceLine[];
  const plans = (plansData?.data ?? []) as ServicePlan[];
  const users = ((usersData?.data ?? []) as UserRow[]).filter((u) => u.isActive);
  const canListUsers = usersData?.success === true;

  const selectedPlan = plans.find((p) => p.id === planId);
  // A one-time engagement produces a single cycle and then stops, so the
  // recurring language ("monthly price", "cycle day") would be wrong.
  const oneTime = selectedPlan?.billing_frequency === 'one_time';

  const create = useMutation({
    mutationFn: () =>
      api.createEngagement({
        accountId,
        serviceLineId,
        planId: planId || null,
        assignedToId: assignedToId || null,
        startDate,
        monthlyPrice: monthlyPrice === '' ? null : Number(monthlyPrice),
      }),
    onSuccess: (r) => {
      if (!r.success) {
        toast({ title: 'Could not create the engagement', description: r.error?.message, variant: 'destructive' });
        return;
      }
      qc.invalidateQueries({ queryKey: ['svc'] });
      toast({ title: 'Engagement created', description: 'Generate its first cycle to create the work.' });
      onOpenChange(false);
      setAccountId(''); setServiceLineId(''); setPlanId(''); setAssignedToId(''); setMonthlyPrice('');
    },
  });

  const ready = accountId && serviceLineId && startDate;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New engagement</DialogTitle>
          <DialogDescription>
            Put a client on a recurring service plan. The plan decides what work each
            period contains.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Field label="Client">
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger><SelectValue placeholder="Select a client…" /></SelectTrigger>
              <SelectContent>
                {accounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {accounts.length === 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                No clients yet. Add one under Customer Operations → Accounts first.
              </p>
            )}
          </Field>

          <Field label="Service line">
            <Select
              value={serviceLineId}
              onValueChange={(v) => { setServiceLineId(v); setPlanId(''); }}
            >
              <SelectTrigger><SelectValue placeholder="Select a service…" /></SelectTrigger>
              <SelectContent>
                {lines.map((l) => (
                  <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Plan" hint="Optional, but an engagement with no plan generates no work.">
            <Select value={planId} onValueChange={setPlanId} disabled={!serviceLineId}>
              <SelectTrigger>
                <SelectValue placeholder={serviceLineId ? 'Select a plan…' : 'Pick a service line first'} />
              </SelectTrigger>
              <SelectContent>
                {plans.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name} (v{p.version})
                    {p.billing_frequency === 'one_time' ? ' — one-time' : ''}
                    {' — '}{Number(p.planned_hours).toFixed(2)}h
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Start date"
              hint={oneTime ? 'When the build begins.' : 'First of the month covers a full period.'}
            >
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </Field>
            <Field
              label={oneTime ? 'Project fee' : 'Monthly price'}
              hint={selectedPlan?.list_price != null ? `Plan lists ${selectedPlan.list_price}` : 'Defaults to the plan price'}
            >
              <Input
                type="number" min={0} step="0.01" value={monthlyPrice}
                onChange={(e) => setMonthlyPrice(e.target.value)}
                placeholder={selectedPlan?.list_price != null ? String(selectedPlan.list_price) : '299.00'}
              />
            </Field>
          </div>

          <Field
            label="Assigned to"
            hint={canListUsers
              ? 'Who delivers the recurring work.'
              : 'Assignable from the engagement once created.'}
          >
            <Select value={assignedToId} onValueChange={setAssignedToId} disabled={!canListUsers}>
              <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.firstName} {u.lastName} ({u.role})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          {oneTime && (
            <p className="text-xs text-muted-foreground border-t pt-3">
              One-time: this produces a single set of work when you generate its cycle,
              then stops. It never recurs and the daily sweep will not pick it up.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => create.mutate()} disabled={!ready || create.isPending}>
            Create engagement
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, hint, children }: {
  label: string; hint?: string; children: React.ReactNode;
}) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="mt-1">{children}</div>
      {hint && <p className="text-[11px] text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}
